import { useEffect, useState } from 'react';
import { useT } from '@/infra/i18n';
import { eventBus } from '@/infra/event-bus/EventBus';
import { COLORS, SPACING, RADIUS, FONT } from '@/presentation/theme/tokens';
import { overlayStyle, cardStyle } from '@/presentation/modals/modal-shared';

interface SummaryData {
  outcome: 'extracted' | 'fainted' | 'died';
  kills: number;
  xp: number;
}

export function RunSummaryModal() {
  const t = useT();
  const [data, setData] = useState<SummaryData | null>(null);

  useEffect(() => {
    const unsub = eventBus.on('combat.ended', (p) => {
      if (p.outcome === 'in-progress') return;
      setData({ outcome: p.outcome, kills: p.kills, xp: p.xp });
    });
    return unsub;
  }, []);

  if (!data) return null;

  const outcomeColor =
    data.outcome === 'extracted' ? COLORS.ok : data.outcome === 'fainted' ? COLORS.warn : COLORS.danger;

  return (
    <div role="dialog" data-testid="run-summary" style={overlayStyle} onClick={() => setData(null)}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ ...titleStyle, color: outcomeColor }}>
          {t('expedition.summary.title')}
        </h2>
        <p style={{ ...outcomeText, color: outcomeColor }}>
          {t(`expedition.outcome.${data.outcome}`)}
        </p>
        <div style={statsRow}>
          <span>{t('expedition.summary.kills', { n: String(data.kills) })}</span>
          <span>{t('expedition.summary.xp', { n: String(data.xp) })}</span>
        </div>
        <button
          data-testid="summary-close"
          onClick={() => setData(null)}
          style={closeBtn}
        >
          {t('expedition.summary.close')}
        </button>
      </div>
    </div>
  );
}

const titleStyle: React.CSSProperties = {
  margin: `0 0 ${SPACING.sm}px 0`,
  fontSize: FONT.sizeXl,
  textAlign: 'center',
};
const outcomeText: React.CSSProperties = {
  fontSize: FONT.sizeMd,
  textAlign: 'center',
  marginBottom: SPACING.lg,
};
const statsRow: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: SPACING.xs,
  fontSize: FONT.sizeSm,
  color: COLORS.text,
  marginBottom: SPACING.lg,
  fontFamily: FONT.mono,
};
const closeBtn: React.CSSProperties = {
  width: '100%',
  background: COLORS.accent,
  color: '#000',
  border: 'none',
  borderRadius: RADIUS.md,
  padding: '8px 14px',
  cursor: 'pointer',
  fontWeight: 600,
};
